import { useCallback, useState, useEffect } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import Fab from '@mui/material/Fab'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { useTheme, lighten } from '@mui/material/styles'
import { arrayOf, bool, element, func, number, oneOfType } from 'prop-types'
import { useHistory } from 'react-router-dom'
import { colors } from '@global/constants'
import { t } from '@lingui/core/macro'

export const EnhancedTableToolbar = ({
  filterChanged,
  numSelected = 0,
  term = '',
  noAdd,
  addItem = () => {},
  children,
  resultsDrawer = false,
  specialAdd = null,
  filteringFields,
  noPadding,
}) => {
  const theme = useTheme()
  const history = useHistory()

  const highlightStyle =
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        }

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (!mounted) {
      if (history.location.search && !resultsDrawer) {
        const existingSearch = new URLSearchParams(history.location.search).get('q')

        if (existingSearch !== term) {
          filterChanged(existingSearch)
        }
      }
    }

    setMounted(true)
  }, [filterChanged, mounted, term])

  const handleChange = useCallback(
    change => {
      filterChanged(change.target.value)
    },
    [filterChanged],
  )

  const handleClearSearch = useCallback(() => {
    filterChanged('')
  }, [filterChanged])

  const handleAddItem = useCallback(() => {
    addItem()
  }, [])

  return (
    <Toolbar
      sx={{
        ...(noPadding
          ? {
              padding: 0,
              minHeight: 52,
            }
          : {
              paddingLeft: theme.spacing(3),
              paddingRight: theme.spacing(3),
            }),
        ...(numSelected > 0 && highlightStyle),
      }}
      data-test-cmp-id="table-toolbar"
    >
      <div style={{ flex: '0 0 auto' }}>
        {numSelected > 0 ? (
          <Typography color="textPrimary" variant="subtitle1" style={{ paddingLeft: 24 }}>
            {numSelected} {t`Selected`}
          </Typography>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <TextField
              name="search"
              label={t`Search`}
              variant="outlined"
              margin="dense"
              onChange={handleChange}
              value={term || ''}
              placeholder={t`Search`}
              InputProps={{
                sx: {
                  width: '100%',
                  minWidth: '140px',
                  height: 40,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: term ? (
                  <InputAdornment position="end">
                    <ClearIcon style={{ cursor: 'pointer' }} onClick={handleClearSearch} />
                  </InputAdornment>
                ) : null,
              }}
            />
            {filteringFields || ''}
          </div>
        )}
      </div>
      <div style={{ flex: '1 1 100%' }} />
      <div style={{ color: theme.palette.text.secondary }}>
        {numSelected > 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {children}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {' '}
            {specialAdd ? <div style={{ marginRight: 8 }}>{specialAdd}</div> : null}
            {noAdd ? null : (
              <div>
                <Fab
                  size="small"
                  aria-label="Add"
                  sx={{
                    backgroundColor: colors.blue.dark,
                    color: colors.white,
                    '&:hover': {
                      backgroundColor: colors.blue.dark,
                    },
                    boxShadow: 'none',
                  }}
                  onClick={handleAddItem}
                  name="add"
                >
                  <AddIcon />
                </Fab>
              </div>
            )}
          </div>
        )}
      </div>
    </Toolbar>
  )
}

EnhancedTableToolbar.propTypes = {
  filterChanged: func.isRequired,
  noAdd: bool.isRequired,
  numSelected: number,
  addItem: func,
  children: oneOfType([arrayOf(element), element]),
  resultsDrawer: bool,
  specialAdd: element,
}
